// Touch-friendly button: a rounded rect + centered label in a container, with
// hover/press feedback. Minimum 48px tall for comfortable touch targets. Used
// by the Title and GameOver scenes.

import Phaser from 'phaser';

export interface ButtonOptions {
  width?: number;
  height?: number;
  fontSize?: number;
  fill?: number;
  fillHover?: number;
  textColor?: string;
}

const DEFAULTS = {
  width: 220,
  height: 56,
  fontSize: 22,
  fill: 0x3a2a12,
  fillHover: 0x5a3f1c,
  textColor: '#ffd9a0',
} as const;

export class Button {
  readonly container: Phaser.GameObjects.Container;
  private bg: Phaser.GameObjects.Rectangle;
  private label: Phaser.GameObjects.Text;

  constructor(
    scene: Phaser.Scene,
    x: number,
    y: number,
    text: string,
    onClick: () => void,
    opts: ButtonOptions = {},
  ) {
    const o = { ...DEFAULTS, ...opts };
    this.bg = scene.add
      .rectangle(0, 0, o.width, Math.max(48, o.height), o.fill)
      .setStrokeStyle(2, 0xffb347, 0.6);
    this.label = scene.add
      .text(0, 0, text, {
        fontFamily: 'system-ui',
        fontSize: `${o.fontSize}px`,
        color: o.textColor,
      })
      .setOrigin(0.5);

    this.container = scene.add.container(x, y, [this.bg, this.label]).setDepth(20);
    this.bg.setInteractive({ useHandCursor: true });
    this.bg.on('pointerover', () => this.bg.setFillStyle(o.fillHover));
    this.bg.on('pointerout', () => this.bg.setFillStyle(o.fill));
    this.bg.on('pointerdown', () => this.container.setScale(0.96));
    this.bg.on('pointerup', () => {
      this.container.setScale(1);
      onClick();
    });
  }

  setPosition(x: number, y: number): this {
    this.container.setPosition(x, y);
    return this;
  }

  setText(text: string): this {
    this.label.setText(text);
    return this;
  }

  destroy(): void {
    this.container.destroy();
  }
}
